<template>
  <div class="conferences">
    <div class="page-header">
      <h1>College Football Conferences</h1>
    </div>

    <div v-if="isLoading" class="loading">
      <i class="fas fa-spinner fa-spin"></i>
      <p>Loading conferences...</p>
    </div>

    <div v-else-if="conferences.length === 0" class="no-conferences">
      <i class="fas fa-calendar-times"></i>
      <p>No conference data available</p>
    </div>

    <div v-else class="conferences-container">
      <div
        v-for="conference in conferences"
        :key="conference.name"
        class="conference-card"
      >
        <div class="conference-header">
          <h2>{{ conference.name }}</h2>
          <span class="team-count">{{ conference.teams.length }} teams</span>
        </div>

        <div class="teams-grid">
          <div
            v-for="team in conference.teams"
            :key="team.id"
            class="team-item"
            @click="goToTeamDetail(team.id)"
          >
            <div class="team-logo">
              <img
                v-if="team.logo"
                :src="team.logo"
                :alt="team.short_display_name || team.display_name || team.abbrev || 'Team Logo'"
                @error="handleImageError"
              />
              <div v-else class="logo-placeholder">
                {{ getTeamInitial(team) }}
              </div>
            </div>
            <div class="team-info">
              <div class="team-name">{{ team.short_display_name || team.display_name || team.abbrev || 'Unknown Team' }}</div>
              <div v-if="team.rank && team.rank < 99" class="team-rank">
                #{{ team.rank }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getTeamInitial, handleImageError, transformTeamData } from "../utils/teamUtils";

export default {
  name: "ConferencesView",
  data() {
    return {
      conferences: [],
      isLoading: false,
    };
  },
  created() {
    this.fetchConferences();
  },
  methods: {
    getTeamInitial,
    handleImageError,
    async fetchConferences() {
      this.isLoading = true;
      try {
        // Use the teams data service
        const { default: dataService } = await import("../utils/dataService");
        const teamsData = await dataService.loadTeamsData();

        // Transform teams data and group by conference
        const conferencesMap = new Map();

        teamsData.forEach((team) => {
          const conferenceName = team.conference || "Independent";

          if (!conferencesMap.has(conferenceName)) {
            conferencesMap.set(conferenceName, {
              name: conferenceName,
              teams: [],
            });
          }

          conferencesMap.get(conferenceName).teams.push(transformTeamData(team));
        });

        // Convert to array and sort teams within each conference
        this.conferences = Array.from(conferencesMap.values());

        // Filter out invalid teams and sort
        this.conferences.forEach((conference) => {
          // Filter out invalid teams
          conference.teams = conference.teams.filter(team =>
            team && team.id && (team.short_display_name || team.display_name || team.abbrev)
          );

          conference.teams.sort((a, b) => {
            // Sort by rank first, then by name
            if (a.rank !== b.rank) {
              return a.rank - b.rank;
            }
            const nameA = a.short_display_name || a.display_name || a.abbrev || 'Unknown';
            const nameB = b.short_display_name || b.display_name || b.abbrev || 'Unknown';
            return nameA.localeCompare(nameB);
          });
        });

        // Sort conferences by name (put Independent first)
        this.conferences.sort((a, b) => {
          if (a.name === "Independent") return -1;
          if (b.name === "Independent") return 1;
          const nameA = a.name || 'Unknown';
          const nameB = b.name || 'Unknown';
          return nameA.localeCompare(nameB);
        });
      } catch (error) {
        console.error("Error fetching conferences:", error);
        this.conferences = [];
      } finally {
        this.isLoading = false;
      }
    },
    goToTeamDetail(teamId) {
      // This would navigate to a team detail page
      // For now, we'll just log the team ID
      console.log(`Navigate to team detail for ID: ${teamId}`);
    },
  },
};
</script>

<style scoped>
.conferences {
  width: 100%;
}

.page-header {
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #30363d;
}

.page-header h1 {
  color: #c9d1d9;
  font-size: 2rem;
}

.loading,
.no-conferences {
  text-align: center;
  padding: 3rem;
  background-color: #161b22;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  border: 1px solid #30363d;
}

.loading i,
.no-conferences i {
  font-size: 3rem;
  color: #ff7b45;
  margin-bottom: 1rem;
}

.conferences-container {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.conference-card {
  background-color: #161b22;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  border: 1px solid #30363d;
}

.conference-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  background-color: #0d1117;
  color: #c9d1d9;
  border-bottom: 1px solid #30363d;
}

.conference-header h2 {
  font-size: 1.5rem;
  margin: 0;
}

.team-count {
  background-color: #238636;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.875rem;
  color: white;
}

.teams-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
  padding: 1.5rem;
}

.team-item {
  display: flex;
  align-items: center;
  padding: 0.75rem;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.team-item:hover {
  background-color: #21262d;
}

.team-logo {
  width: 40px;
  height: 40px;
  margin-right: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.team-logo img {
  max-width: 100%;
  max-height: 100%;
}

.logo-placeholder {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #4a5568, #2d3748);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 16px;
  color: #e2e8f0;
  text-transform: uppercase;
}

.team-info {
  flex: 1;
}

.team-name {
  font-weight: 500;
  margin-bottom: 0.25rem;
  color: #e0e0e0;
}

.team-rank {
  display: inline-block;
  background-color: #ff7b45;
  color: white;
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.125rem 0.375rem;
  border-radius: 4px;
}

@media (max-width: 768px) {
  .teams-grid {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  }
}
</style>
